import React, { useState, useEffect, FC } from "react";
import AddressRow from "./AddressRow";
import Spinner from "./Spinner";
import "../scss/AddressCompact.scss";
import { useApi } from "../api/ApiProvider";
import Api from "../axiosApi/api";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import ModalAddress from "./ModalAddress";
import { PermissionsGate } from "./PermissionsGate";
import { HiOutlineMailOpen, HiOutlinePlus } from "react-icons/hi";
import intl from "components/i18n/ReactIntlWrapper";
import { handleAPIError } from "../common/errorHandler";
import { Label } from "reactstrap";

interface AddressCompactProps {
  disabled: boolean;
  addressesId?: number[];
  personaExist?: boolean;
  setAddressesId?: Function;
  editScopes?: string[];
  deleteScopes?: string[];
  allowOne?: boolean;
  inputProvider?: any;
  setInputProvider?: (value: any) => void;
  realAddress?: boolean;
  legalAddress?: boolean;
  corporateEntityAddresses?: any;
  input?: any;
  setInput?: (value: any) => void;
  setErrosCorporateEntity?: Function;
}

const AddressCompact: FC<AddressCompactProps> = (props) => {
  const {
    addressesId = null,
    setAddressesId = null,
    personaExist = false,
    editScopes,
    deleteScopes,
    disabled,
    allowOne = false,
    inputProvider = null,
    setInputProvider = null,
    realAddress = false,
    legalAddress = false,
    corporateEntityAddresses = null,
    input = null,
    setInput = null,
    setErrosCorporateEntity = null,
  } = props;

  const api: Api = useApi();

  const [addresses, setAddresses] = useState([]);

  const [adressExist, setAddressExist] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);

  const toast = useToastMessageQueue();

  const [isAddressDetailOpen, setIsAddressDetailOpen] = useState(false);

  const [errors, setErrors] = useState({});

  const fetchAddresses = async (addressesId: number[]) => {
    setLoading(true);
    const response = await api.addressApi
      .apiVversionAddressAddressesGet("1", addressesId, {})
      .then((response) => {
        if (response.data) {
          setAddresses(response.data?.data);
          setLoading(false);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  useEffect(() => {
    if (
      addressesId?.length > 0 &&
      addressesId !== null &&
      addressesId[0] !== null
    ) {
      fetchAddresses(addressesId);
    }
  }, [addressesId]);

  useEffect(() => {
    if (addresses) {
      addresses.length > 0 ? setAddressExist(true) : setAddressExist(false);
    }
    if (corporateEntityAddresses) {
      corporateEntityAddresses.length > 0
        ? setAddressExist(true)
        : setAddressExist(false);
    }
  }, [addresses, corporateEntityAddresses]);

  const closeAddressDetail = () => setIsAddressDetailOpen(false);

  const handleAddressDetailOpen = () => {
    setIsAddressDetailOpen(true);
    setErrosCorporateEntity({});
  };

  return (
    <>
      {realAddress ? (
        <div className="row">
          <div className="col">
            <Label for="realAddress">* {intl.get("address.real")}</Label>
            <p className="fw-light purpose mb-0">
              {intl.get("address.billingPurpose")}
            </p>
          </div>
          <div className="col text-end">
            <button
              type="submit"
              className="btn btn-primary newAddress"
              disabled={disabled || (allowOne && adressExist)}
              onClick={handleAddressDetailOpen}
            >
              <HiOutlinePlus className="mb-1" />{" "}
              {intl.get("addressCompact.newAddress.button")}
            </button>
            <ModalAddress
              isOpen={isAddressDetailOpen}
              closeModal={closeAddressDetail}
              addressId={null}
              addresses={addresses}
              setAddresses={setAddresses}
              addressesId={addressesId}
              setAddressesId={setAddressesId}
              realAddress={realAddress}
              input={input}
              setInput={setInput}
            />
          </div>
        </div>
      ) : legalAddress ? (
        <div className="row">
          <div className="col">
            <Label for="legalAddress" className="mb-0">
              * {intl.get("address.legal")}
            </Label>
          </div>
          <div className="col text-end">
            <button
              type="submit"
              className="btn btn-primary newAddress"
              disabled={disabled || (allowOne && adressExist)}
              onClick={handleAddressDetailOpen}
            >
              <HiOutlinePlus className="mb-1" />{" "}
              {intl.get("addressCompact.newAddress.button")}
            </button>
            <ModalAddress
              isOpen={isAddressDetailOpen}
              closeModal={closeAddressDetail}
              addressId={null}
              addresses={addresses}
              setAddresses={setAddresses}
              addressesId={addressesId}
              setAddressesId={setAddressesId}
              legalAddress={legalAddress}
              input={input}
              setInput={setInput}
            />
          </div>
        </div>
      ) : (
        <div className="card-header">
          <h4 className="title">
            <HiOutlineMailOpen className="mb-1" />{" "}
            {intl.get("addressCompact.header")}
          </h4>
        </div>
      )}
      {personaExist && addresses.length < 1 ? (
        <div className="alert alert-warning">
          {intl.get("addBilling.alert")}
        </div>
      ) : null}
      {loading === true ? (
        <Spinner small={true} />
      ) : (
        <div className="card-body">
          <div className="table-responsive container-overflow">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.address1")}
                  </th>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.address2")}
                  </th>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.country")}
                  </th>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.stateProvince")}
                  </th>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.city")}
                  </th>
                  <th scope="col">
                    {intl.get("addressCompact.table.thead.zipCode")}
                  </th>
                  <th scope="col" className="text-center">
                    {intl.get("addressCompact.table.thead.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {addresses?.map((e, i) => (
                  <AddressRow
                    key={e.id}
                    addressId={e.id}
                    address1={e.address1}
                    address2={e.address2}
                    city={e.city}
                    stateProvinceName={e.stateProvince?.name}
                    zipCode={e.zipCode}
                    countryName={
                      e.country?.labelKey
                        ? intl.get(e.country.labelKey)
                        : e.country?.name
                    }
                    editScopes={editScopes}
                    deleteScopes={deleteScopes}
                    disabled={disabled}
                    addresses={addresses}
                    setAddresses={setAddresses}
                    inputProvider={inputProvider}
                    setInputProvider={setInputProvider}
                    className={i % 2 > 0 ? "" : "odd"}
                    errors={errors}
                    setErrors={setErrors}
                  />
                ))}
                {corporateEntityAddresses?.map((e, i) => (
                  <AddressRow
                    key={e?.address?.id}
                    addressId={e?.address?.id}
                    address1={e?.address?.address1}
                    address2={e?.address?.address2}
                    city={e?.address?.city}
                    stateProvinceName={e?.address?.stateProvince?.name}
                    zipCode={e?.address?.zipCode}
                    countryName={
                      e?.address?.country?.labelKey
                        ? intl.get(e?.address?.country.labelKey)
                        : e?.address?.country?.name
                    }
                    editScopes={editScopes}
                    deleteScopes={deleteScopes}
                    disabled={disabled}
                    className={i % 2 > 0 ? "" : "odd"}
                    errors={errors}
                    setErrors={setErrors}
                    realAddress={realAddress}
                    legalAddress={legalAddress}
                    input={input}
                    setInput={setInput}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {!realAddress && !legalAddress ? (
            <PermissionsGate
              viewScopes={editScopes}
              editScopes={editScopes}
              viewRoles={[]}
              editRoles={[]}
            >
              <div className="text-center mt-2">
                <button
                  type="submit"
                  className="btn btn-primary newAddress"
                  disabled={disabled || (allowOne && adressExist)}
                  onClick={handleAddressDetailOpen}
                >
                  <HiOutlinePlus className="mb-1" />{" "}
                  {intl.get("addressCompact.newAddress.button")}
                </button>
                <ModalAddress
                  isOpen={isAddressDetailOpen}
                  closeModal={closeAddressDetail}
                  addressId={null}
                  addresses={addresses}
                  setAddresses={setAddresses}
                  addressesId={addressesId}
                  setAddressesId={setAddressesId}
                  inputProvider={inputProvider}
                  setInputProvider={setInputProvider}
                />
              </div>
            </PermissionsGate>
          ) : null}
        </div>
      )}
    </>
  );
};

export default AddressCompact;
